import {
  Box,
  ButtonDropdown,
  ButtonDropdownProps,
  FormField,
  Icon,
  InputProps,
  NonCancelableCustomEvent,
  Popover,
  Table,
  Textarea,
} from '@amzn/awsui-components-core';
import { ContentAudience, WorkflowState } from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import { contentAudienceBadgeDisplayNames } from '../utils/DisplayNames';
import ContentAudienceBadge from '../workflow-viewer/ContentAudienceBadge';
import WorkflowStateBadge from '../workflow-viewer/WorkflowStateBadge';

export interface DemoteAudienceInfoLineItemWorkflow {
  contentId: string;
  contentName: string;
  version: number;
  audience: ContentAudience;
  currentState: WorkflowState;
}

interface DemoteAudienceInfoProps {
  workflowItems: DemoteAudienceInfoLineItemWorkflow[];
  targetAudience: ContentAudience;
  validationError?: string;
  onAudienceChanged?: (newAudience: ContentAudience) => void;
  onReasonChanged?: (reason: string, validationError: boolean) => void;
}

const getNewState = (currentState: WorkflowState, newAudience: ContentAudience) => {
  const stateMap = new Map<ContentAudience, Map<WorkflowState, WorkflowState>>([
    [
      ContentAudience.INTERNAL,
      new Map([
        [WorkflowState.FINALIZED, WorkflowState.PUBLISHED],
        [WorkflowState.FINALIZING, WorkflowState.PUBLISHED],
        [WorkflowState.DISTRIBUTED, WorkflowState.PUBLISHED],
        [WorkflowState.REVOKED, WorkflowState.PUBLISHED],
      ]),
    ],
    [ContentAudience.EXTERNAL, new Map([[WorkflowState.DISTRIBUTED, WorkflowState.FINALIZED]])],
  ]);

  const newState = stateMap.get(newAudience)?.get(currentState) ?? currentState;

  return newState;
};

const filterWorkflowItems = (
  items: DemoteAudienceInfoLineItemWorkflow[],
  newAudience: ContentAudience,
) => {
  return items.filter((wfi) =>
    [wfi.audience !== newAudience, wfi.currentState !== WorkflowState.UNMODIFIED].every((c) => c),
  );
};

export const DemoteAudienceInfo = (props: DemoteAudienceInfoProps) => {
  const [newAudience, setNewAudience] = useState<ContentAudience>(props.targetAudience);
  const [items, setItems] = useState(filterWorkflowItems(props.workflowItems, newAudience));
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  useEffect(() => {
    // @ts-expect-error TS2345 - strict migration
    setReasonError(props.validationError);
  }, [props.validationError]);

  const colDef = [
    {
      // @ts-expect-error TS7030 - strict migration
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => {
        if (
          item.currentState === WorkflowState.FINALIZING &&
          getNewState(item.currentState, newAudience) === WorkflowState.PUBLISHED
        ) {
          const popoverContent = <div>Pending Tech Writer changes will be deleted.</div>;
          return (
            <Popover
              position="right"
              size="large"
              triggerType="custom"
              content={popoverContent}
              dismissButton={false}
            >
              <Icon name="status-warning" size="small" variant="warning" />
            </Popover>
          );
        }

        if (
          item.currentState === WorkflowState.DISTRIBUTED &&
          getNewState(item.currentState, newAudience) !== WorkflowState.DISTRIBUTED
        ) {
          const popoverContent = (
            <div>
              Content distributed to APG will be revoked but not automatically removed from APG.
              Make sure this changes is coordinated with a Tech Writer.
            </div>
          );
          return (
            <Popover
              position="right"
              size="large"
              triggerType="custom"
              content={popoverContent}
              dismissButton={false}
            >
              <Icon name="status-info" size="small" />
            </Popover>
          );
        }
      },
      header: '',
      id: 'demoteState',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => item.contentName,
      header: 'Content',
      id: 'contentName',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => item.version,
      header: 'Version',
      id: 'version',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => (
        <WorkflowStateBadge state={item.currentState} />
      ),
      header: 'Current State',
      id: 'currentState',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => (
        <WorkflowStateBadge state={getNewState(item.currentState, newAudience)} />
      ),
      header: 'New State',
      id: 'newState',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => (
        <ContentAudienceBadge contentAudience={item.audience} />
      ),
      header: 'Current Audience',
      id: 'currentAudience',
    },
    {
      cell: (item: DemoteAudienceInfoLineItemWorkflow) => (
        <ContentAudienceBadge contentAudience={newAudience} />
      ),
      header: 'New Audience',
      id: 'newAudience',
    },
  ];

  const newContentAudienceSelection = [
    {
      id: ContentAudience.INTERNAL,
      text: contentAudienceBadgeDisplayNames.get(ContentAudience.INTERNAL),
    },
    {
      id: ContentAudience.EXTERNAL,
      text: contentAudienceBadgeDisplayNames.get(ContentAudience.EXTERNAL),
    },
  ];

  const updateNewAudience = (
    event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    // @ts-expect-error TS7053 - strict migration
    const updatedAudience = ContentAudience[event.detail.id];
    setNewAudience(updatedAudience);

    if (props.onAudienceChanged) {
      props.onAudienceChanged(updatedAudience);
    }

    const newItems = filterWorkflowItems(props.workflowItems, updatedAudience);
    setItems(newItems);
  };

  const updateReason = (e: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    if (e.detail.value.length < 2000) {
      setReason(e.detail.value);
      const validationError = !e.detail.value ? 'Reason to demote is required' : undefined;
      // @ts-expect-error TS2345 - strict migration
      setReasonError(validationError);
      props.onReasonChanged?.(e.detail.value, !validationError);
    }
  };

  return (
    <div>
      <Box variant="p">
        Demoting the intended audience might change the state of already published content and has
        some other implications on content accessiblity. Please carefully review the summary of
        changes table before submitting.
      </Box>
      <Box variant="p">
        All future versions of demoted content will also use the demoted audience unless it is
        changed again by the author.
      </Box>
      <Box margin={{ vertical: 'l' }}>
        <FormField label="Demote Intended Audience to">
          {/* @ts-expect-error TS2322 - strict migration */}
          <ButtonDropdown items={newContentAudienceSelection} onItemClick={updateNewAudience}>
            {contentAudienceBadgeDisplayNames.get(newAudience)}
          </ButtonDropdown>
        </FormField>
      </Box>
      <Box margin={{ vertical: 'l' }}>
        <Table columnDefinitions={colDef} items={items} wrapLines />
      </Box>
      <Box margin={{ vertical: 'l' }}>
        <FormField
          label="Reason"
          stretch
          errorText={reasonError}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Textarea onChange={updateReason} value={reason} rows={3} ariaRequired />
        </FormField>
      </Box>
    </div>
  );
};
